<template>
  <net-popup-menu>
    <template #title>{{ $t("objectNames.index") }}</template>

    <input-texteditor
      ref="descriptionField"
      :label="$t('labels.description')"
      :value="ctx.index.description"
      @update:value="ctx.updateKey('description', $event)"
    />

    <div>
      <h4 class="label mt-0">{{ $t("labels.value") }}</h4>
      <expression-input
        :expression="ctx.index.valueExpression"
        :parse-context="{
          affectedType: 'index',
          affectedId: props.indexId,
          rounds: [
            { elementValues }
          ]
        }"
        @update:expression="ctx.updateKey('valueExpression', $event)"
      />
    </div>

    <div>
      <h3 class="label mt-0">{{ $t("objectNames.stakeholder", 4) }}</h3>
      <stakeholder-list
        item-type="index"
        :item-id="ctx.index.id"
        :selected-stakeholder-ids="[]"
        :select-enabled="false"
        @select="emit('select', $event)"
      />
    </div>

    <div class="column gap-4">
      <input-switch
        :value="ctx.treeNode?.visible"
        @update:value="ctx.updateNodeKey('visible', $event)"
      >
        {{ $t("labels.visible") }}
      </input-switch>

      <template v-if="ctx.treeNode && ctx.treeNode.displayPosition === 'scene' && ctx.sceneNode">
        <fieldset class="column menu-fieldset">
          <legend class="label mb-0">{{ $t("labels.valueStyle") }}</legend>
          <div class="column gap-2">
            <input-switch
              :value="ctx.sceneNode.data.valueVisible"
              @update:value="ctx.updateNodeDataKey('valueVisible', $event)"
            >
              {{ $t("labels.elementValueVisible") }}
            </input-switch>
            <input-switch
              :value="ctx.sceneNode.data.barVisible"
              @update:value="ctx.updateNodeDataKey('barVisible', $event)"
            >
              {{ $t("labels.elementBarVisible") }}
            </input-switch>
          </div>
          <input-switch
            v-if="ctx.sceneNode.data.barVisible"
            :value="ctx.sceneNode.data.barWidthLimited"
            @update:value="ctx.updateNodeDataKey('barWidthLimited', $event)"
          >
            {{ $t("labels.barWidthLimited") }}
          </input-switch>
        </fieldset>

        <!-- hide title -->
        <input-switch
          :value="ctx.sceneNode.data.headlineHidden"
          :label="$t('labels.hideElementTitle')"
          :text-on="$t('labels.yes')"
          :text-off="$t('labels.no')"
          @update:value="ctx.updateNodeDataKey('headlineHidden', $event)"
        />
      </template>
    </div>

    <button
      class="button button_secondary mv-2"
      type="button"
      @click="ctx.delete()"
    >
      {{ $t("actions.deleteIndex", 1) }}
    </button>
  </net-popup-menu>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import InputTexteditor from '@core/input/TextEditor.vue';
import InputSwitch from '@core/input/InputSwitch.vue';

import NetPopupMenu from '@editor/net/NetPopupMenu.vue';
import ExpressionInput from '@editor/items/expression/ExpressionInput.vue';
import StakeholderList from '@editor/items/stakeholder/StakeholderList.vue';

import { useIndex } from './useIndex';
import { useElements } from '../element/elements';
import type { Uid } from '@/helpers/utility/Id';

const props = defineProps<{
  indexId: number;
}>();

const emit = defineEmits<{
  (e: "select", item: { uid: Uid<"stakeholder">}): void;
  (e: "deselect", item: { uid: Uid<"stakeholder"> }): void;
}>();

const ctx = useIndex(() => props.indexId);

const elementValues = computed(() => useElements().elementValues);
</script>

<style></style>